import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner, Alert, InputGroup } from "react-bootstrap";
import toastStore from "../../store/ToastStore";
import { getDigestSettings, saveDigestSettings, suggestDigestThreshold, sendTestDigest } from "../../api/digestApi";

function extractErrorMessage(e, fallback) {
  return e?.response?.data?.message || e?.message || fallback;
}

export default function DigestSettingsModal({ server, show, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [email, setEmail] = useState("");
  const [threshold, setThreshold] = useState(20);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!server?.id) return;
    setLoading(true);
    setError(null);
    getDigestSettings(server.id)
      .then((data) => {
        setEnabled(Boolean(data.enabled));
        setEmail(data.email || "");
        setThreshold(Number(data.alert_threshold_pct) || 20);
      })
      .catch((e) => setError(e))
      .finally(() => setLoading(false));
  }, [server?.id]);

  useEffect(() => {
    if (show) load();
  }, [show, load]);

  const handleSave = async () => {
    if (enabled && !email.trim()) {
      toastStore.error("Укажите email для дайджеста.");
      return;
    }
    setSaving(true);
    try {
      await saveDigestSettings(server.id, { enabled, email: email.trim(), alertThresholdPct: threshold });
      toastStore.success("Настройки дайджеста сохранены.");
      onClose();
    } catch (e) {
      toastStore.error(extractErrorMessage(e, "Не удалось сохранить настройки дайджеста."));
    } finally {
      setSaving(false);
    }
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    try {
      const { suggested_threshold_pct } = await suggestDigestThreshold(server.id);
      setThreshold(suggested_threshold_pct);
      toastStore.success(`Предложенный порог: ${suggested_threshold_pct}%`);
    } catch (e) {
      toastStore.error(extractErrorMessage(e, "Не удалось предложить порог."));
    } finally {
      setSuggesting(false);
    }
  };

  const handleSendTest = async () => {
    if (!email.trim()) {
      toastStore.error("Сначала укажите email.");
      return;
    }
    setSendingTest(true);
    try {
      await saveDigestSettings(server.id, { enabled, email: email.trim(), alertThresholdPct: threshold });
      await sendTestDigest(server.id);
      toastStore.success(`Тестовое письмо отправлено на ${email.trim()}.`);
    } catch (e) {
      toastStore.error(extractErrorMessage(e, "Не удалось отправить тестовое письмо."));
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Email-дайджест — {server?.name || server?.server}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-body-secondary py-3">
            <Spinner animation="border" size="sm" />
            Загрузка…
          </div>
        ) : (
          <Form>
            {error && <Alert variant="danger">Не удалось загрузить настройки.</Alert>}
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="digest-enabled"
                label="Присылать ежедневный AI-дайджест по почте"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <Form.Text className="text-body-secondary">
                Раз в сутки — сводка Hermes за последние 7 завершённых дней. Если есть риски,
                письмо помечается как важное.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email получателя</Form.Label>
              <Form.Control
                type="email"
                placeholder="owner@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Порог значимого отклонения выручки, %</Form.Label>
              <InputGroup>
                <Form.Control
                  type="number"
                  min={5}
                  max={95}
                  value={threshold}
                  onChange={(e) => setThreshold(Number(e.target.value) || 0)}
                />
                <Button variant="outline-secondary" onClick={handleSuggest} disabled={suggesting}>
                  {suggesting ? <Spinner animation="border" size="sm" /> : "Предложить"}
                </Button>
              </InputGroup>
              <Form.Text className="text-body-secondary">
                «Предложить» считает типичный разброс дневной выручки за последние 60 дней —
                без обращения к ИИ, бесплатно.
              </Form.Text>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={handleSendTest} disabled={sendingTest || loading}>
          {sendingTest ? <Spinner animation="border" size="sm" /> : "Отправить тестовое письмо"}
        </Button>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Закрыть
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? <Spinner animation="border" size="sm" /> : "Сохранить"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
